import React, { useCallback, useEffect } from "react";
import { log } from "@qgiv/core-js";
import { useDispatch } from "react-redux";
import { getIsOnePaySubmission } from "@qgiv/donation-form";
import useSinglePageData from "../PageDataHooks/useSinglePageData";
import ConnectedRecurringUpgrade from "../Pages/PaymentPage/RecurringUpgrade";
import { ModalContentReplacerEnums } from "../common/ModalContentReplacer";
import {
    getInitialTouched,
    getInitialValues,
    getValidationSchema,
} from "./formik";
import { setAbandonedGiftDataFirstName } from "../../redux/slices/abandonedGiftSlice";
import { setShouldScrollToTop } from "../../redux/slices/appSettingsSlice";
import {
    updateDisplayMinMaxErrorMessage,
    updateSubmitDonation,
    updateValidateDonation,
} from "../../redux/slices/donationDetailsSlice";
import SinglePage from "./SinglePage";

const ConnectedSinglePage = () => {
    const dispatch = useDispatch();
    const singlePageData = useSinglePageData();

    const {
        storedAbandonedGiftData,
        acceptedEmailReminders,
        conditionalLogicOptions,
        displayCaptcha,
        documentTitle,
        formikSettings,
        getShouldSubmitDonation,
        hasActiveFulfillment,
        hasDisplayableDedicationOnAdditionalDetailsPage,
        hasDisplayableMatchingGiftOnAdditionalDetailsPage,
        hasMinMaxValidationError,
        hasOnePay,
        selectedContentToDisplay,
        shouldDisplayRecurringUpgrade,
        storedInitialValuesFromRedux,
        submitDonation,
        useAbandonedGiftData,
        validateOnePayDonation,
    } = singlePageData;

    // -------------------------------------------------------------------------
    // Note: Recipient search, Abandoned Gifts Email Exit Intent are not used in Single Page currently however may
    // be needed in the future if P2P or Auction has a single page option.
    // -------------------------------------------------------------------------
    const getAlternateContent = useCallback(() => {
        const alternateContent = [];
        if (shouldDisplayRecurringUpgrade) {
            alternateContent.push({
                component: <ConnectedRecurringUpgrade />,
                type: ModalContentReplacerEnums.UPGRADE_RECURRING_UI,
            });
        }
        return alternateContent;
    }, [shouldDisplayRecurringUpgrade]);

    const alternateContent = getAlternateContent();

    // Verify that the conditions are right before proceeding to submission
    const onSubmit = (values, formikBag) => {
        // Abort the submission if we encounter validation errors related to
        // data that is being held in Redux instead of Formik. These are errors
        // that we only want to display to users after they attempted to submit
        const hasReduxValidationError = hasMinMaxValidationError;

        if (hasMinMaxValidationError) {
            dispatch(updateDisplayMinMaxErrorMessage(true));
        }

        if (hasReduxValidationError) {
            formikBag.setSubmitting(false);
            dispatch(setShouldScrollToTop(true));
            return;
        }

        const isOnePaySubmission = getIsOnePaySubmission({
            hasOnePay,
            values,
        });

        if (isOnePaySubmission) {
            // we are not showing recurring upgrade for now
            dispatch(updateValidateDonation(true));
        } else {
            getShouldSubmitDonation(values, formikBag)
                .then(({ shouldSubmitDonation }) => {
                    if (shouldSubmitDonation) {
                        dispatch(setShouldScrollToTop(true));
                        dispatch(updateSubmitDonation(true));
                    }
                })
                .catch((errors) => {
                    log(errors);
                });
        }
    };

    const {
        initialTouchedSettings,
        initialValuesSettings,
        validationSettings,
    } = formikSettings;

    // Move this to connected like Gift Page hook
    const formikProps = {
        initialTouched: getInitialTouched(
            hasActiveFulfillment,
            initialTouchedSettings,
            storedInitialValuesFromRedux,
        ),
        initialValues: getInitialValues({
            hasActiveFulfillment,
            initialValuesSettings,
            storedAbandonedGiftData,
            storedInitialValuesFromRedux,
            useAbandonedGiftData,
        }),
        validationSchema: getValidationSchema(
            hasActiveFulfillment,
            validationSettings,
        ),
        onSubmit,
    };

    // Donor account can populate First_Name without triggering onChange
    useEffect(() => {
        const initialFirstName = formikProps?.initialValues?.First_Name || "";
        if (initialFirstName) {
            dispatch(setAbandonedGiftDataFirstName(initialFirstName));
        }
        // we only want this useEffect to run on details page load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    const skipAdditionalDetailsPageProps = {
        conditionalLogicOptions,
        hasActiveFulfillment,
        hasDisplayableDedicationOnAdditionalDetailsPage,
        hasDisplayableMatchingGiftOnAdditionalDetailsPage,
    };

    const singlePageProps = {
        acceptedEmailReminders,
        alternateContent,
        displayCaptcha,
        documentTitle,
        formikProps,
        selectedContentToDisplay,
        skipAdditionalDetailsPageProps,
        submitDonation,
        validateOnePayDonation,
    };

    return <SinglePage {...singlePageProps} />;
};

export default ConnectedSinglePage;
